import React, { useState } from 'react';
import { MapPin, Star, Users, Hand, Tag, CalendarCheck, Minus, Plus, X, Building2, Umbrella, Coffee, CheckCircle2, Navigation, Utensils, Store, Home, Landmark, ShoppingBag, Zap, Car, Clock, Shield, Cross, Pill, Flame, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from './shared';
import { places, placeTypes, nearbyPeople, currentPlace } from '../mock';

const notify = (msg) => toast.success(msg, { style: { background: '#0f1622', color: '#fff', border: '1px solid #1c2634' } });

const TYPE_ICON = { hotel: Building2, beach: Umbrella, cafe: Coffee, famous: Landmark, restaurant: Utensils, food: Store, b2b: Building2, realestate: Home };
const TYPE_LABEL = { hotel: 'hotel', beach: 'beach', cafe: 'cafe', famous: 'famous', restaurant: 'restaurant', food: 'food store', b2b: 'B2B supplier', realestate: 'property' };
const ACTION = {
  hotel: { label: 'Book', Icon: CalendarCheck, book: true },
  restaurant: { label: 'Reserve', Icon: CalendarCheck, msg: 'Table reserved' },
  food: { label: 'Order', Icon: ShoppingBag, msg: 'Order placed — arriving soon' },
  b2b: { label: 'Enquire', Icon: Building2, msg: 'Enquiry sent to supplier' },
  realestate: { label: 'Enquire', Icon: Home, msg: 'Enquiry sent to agent' },
  famous: { label: 'Book Ride', Icon: Car, cab: true },
  beach: { label: 'Visit', Icon: Navigation, msg: 'Visiting' },
  cafe: { label: 'Visit', Icon: Navigation, msg: 'Visiting' },
};

const RIDES = [
  { k: 'Bike', eta: '3 min', price: 45, seats: 1 },
  { k: 'Auto', eta: '5 min', price: 80, seats: 3 },
  { k: 'Sedan', eta: '6 min', price: 150, seats: 4 },
  { k: 'SUV', eta: '8 min', price: 220, seats: 6 },
];

function CabSheet({ destination, onClose }) {
  const [sel, setSel] = useState(null);
  const confirm = () => { onClose(); notify(`${sel.k} booked · arriving in ${sel.eta}`); };
  return (
    <div className="fixed inset-0 z-[65] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-[440px] bg-[#0c1420] rounded-t-3xl p-5 animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-bold text-white">Book a Ride</h3>
          <button onClick={onClose}><X size={22} className="text-[#8b95a5]" /></button>
        </div>
        <p className="text-[12px] text-[#8b95a5] flex items-center gap-1"><MapPin size={12} className="text-[#2f8bff]" /> Pickup: {currentPlace.name}</p>
        {destination && <p className="text-[12px] text-[#8b95a5] flex items-center gap-1 mb-3"><Navigation size={12} className="text-[#22c55e]" /> Drop: {destination.name} · {destination.distance}</p>}
        {!destination && <div className="mb-3" />}
        <div className="space-y-2">
          {RIDES.map((r) => (
            <button key={r.k} onClick={() => setSel(r)} className={`w-full flex items-center gap-3 p-3 rounded-xl ring-1 transition-colors ${sel?.k === r.k ? 'ring-[#2f8bff] bg-[#0f1a2b]' : 'ring-[#1c2634] bg-[#0a0f18]'}`}>
              <div className="w-10 h-10 rounded-full bg-[#141c28] flex items-center justify-center"><Car size={18} className="text-[#2f8bff]" /></div>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-semibold text-white">{r.k}</p>
                <p className="text-[11px] text-[#8b95a5] flex items-center gap-1"><Clock size={11} /> {r.eta} away · {r.seats} seats</p>
              </div>
              <span className="text-[15px] font-bold text-white">₹{r.price}</span>
            </button>
          ))}
        </div>
        <button onClick={confirm} disabled={!sel} className={`w-full mt-4 py-3.5 rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 transition-colors ${sel ? 'bg-[#2f8bff] text-white active:scale-[0.98]' : 'bg-[#141c28] text-[#5c6675]'}`}>
          <Car size={18} /> {sel ? `Confirm ${sel.k} · ₹${sel.price}` : 'Select a ride'}
        </button>
      </div>
    </div>
  );
}

const HELP_ICON = {
  Hospital: { Icon: Cross, cls: 'bg-[#3a1220] text-[#f43f5e]' },
  Police: { Icon: Shield, cls: 'bg-[#122a3a] text-[#2f8bff]' },
  Pharmacy: { Icon: Pill, cls: 'bg-[#12322a] text-[#22c55e]' },
  'Fire Station': { Icon: Flame, cls: 'bg-[#3a2a12] text-[#f5a524]' },
};

function HelpCard({ place }) {
  const { Icon, cls } = HELP_ICON[place.sub] || HELP_ICON.Hospital;
  return (
    <div className="rounded-2xl ring-1 ring-[#141c28] bg-[#0c1420] p-3.5 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${cls}`}><Icon size={22} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-bold text-white truncate">{place.name}</h3>
            <span className="text-[10px] font-semibold text-[#c4ccd8] bg-[#141c28] px-1.5 py-0.5 rounded">{place.sub}</span>
          </div>
          <p className="text-[12px] text-[#8b95a5] flex items-center gap-1"><MapPin size={11} /> {place.area} · {place.distance}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {place.tags.map((t) => <span key={t} className="text-[11px] text-[#c4ccd8] bg-[#141c28] px-2 py-0.5 rounded-full">{t}</span>)}
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => notify(`Calling ${place.name} · ${place.phone}`)} className="flex-1 py-2.5 rounded-lg bg-[#f43f5e] text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
          <Phone size={15} /> Call {place.phone}
        </button>
        <button onClick={() => notify(`Navigating to ${place.name}`)} className="flex-1 py-2.5 rounded-lg ring-1 ring-[#2a3547] text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
          <Navigation size={15} /> Navigate
        </button>
      </div>
    </div>
  );
}

function BookingSheet({ place, onClose }) {
  const [nights, setNights] = useState(2);
  const [guests, setGuests] = useState(2);
  if (!place) return null;
  const total = place.price * nights;
  const book = () => { onClose(); notify(`Booked ${place.name} · ${nights} nights`); };
  return (
    <div className="fixed inset-0 z-[65] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-[440px] bg-[#0c1420] rounded-t-3xl p-5 animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4"><X size={22} className="text-[#8b95a5]" /></button>
        <div className="flex gap-3 items-center">
          <img src={place.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
          <div>
            <h3 className="text-[16px] font-bold text-white">{place.name}</h3>
            <p className="text-[12px] text-[#8b95a5] flex items-center gap-1"><MapPin size={12} /> {place.area}</p>
          </div>
        </div>
        <div className="space-y-3 mt-5">
          {[{ label: 'Nights', v: nights, set: setNights, min: 1 }, { label: 'Guests', v: guests, set: setGuests, min: 1 }].map((r) => (
            <div key={r.label} className="flex items-center justify-between">
              <span className="text-[14px] text-[#c4ccd8]">{r.label}</span>
              <div className="flex items-center gap-3">
                <button onClick={() => r.set((x) => Math.max(r.min, x - 1))} className="w-9 h-9 rounded-full bg-[#141c28] flex items-center justify-center"><Minus size={16} className="text-white" /></button>
                <span className="text-[16px] font-bold text-white w-8 text-center">{r.v}</span>
                <button onClick={() => r.set((x) => x + 1)} className="w-9 h-9 rounded-full bg-[#141c28] flex items-center justify-center"><Plus size={16} className="text-white" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#141c28]">
          <span className="text-[13px] text-[#8b95a5]">₹{place.price.toLocaleString()} x {nights} nights</span>
          <span className="text-[18px] font-bold text-white">₹{total.toLocaleString()}</span>
        </div>
        <button onClick={book} className="w-full mt-4 py-3.5 rounded-xl bg-[#2f8bff] text-white text-[15px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <CalendarCheck size={18} /> Confirm booking
        </button>
      </div>
    </div>
  );
}

function PriceLabel({ place }) {
  if (place.type === 'hotel') return <p className="text-[14px] font-bold text-white shrink-0">₹{place.price.toLocaleString()}<span className="text-[10px] font-normal text-[#8b95a5]">/night</span></p>;
  if (place.type === 'realestate') return <p className="text-[14px] font-bold text-white shrink-0 text-right">₹{place.price.toLocaleString()}<span className="block text-[10px] font-normal text-[#22c55e]">{place.deal}</span></p>;
  if (place.type === 'restaurant') return <p className="text-[12px] text-[#8b95a5] shrink-0">{place.cuisine}</p>;
  if (place.type === 'b2b') return <p className="text-[11px] text-[#8b95a5] shrink-0 text-right">{place.supplier}<span className="block text-[#2f8bff]">MOQ {place.moq}</span></p>;
  return null;
}

function PlaceCard({ place, onBook, onCab }) {
  const Icon = TYPE_ICON[place.type] || MapPin;
  const act = ACTION[place.type] || ACTION.famous;
  const [done, setDone] = useState(false);

  const doAction = () => {
    if (act.book) { onBook(place); return; }
    if (act.cab) { onCab(place); return; }
    setDone(true);
    notify(`${act.msg} · ${place.name}`);
  };

  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-[#141c28] bg-[#0c1420] animate-fade-up">
      <div className="relative">
        <img src={place.image} alt="" className="w-full aspect-[16/9] object-cover" />
        <span className="absolute top-3 left-3 flex items-center gap-1 text-[11px] font-semibold text-white bg-black/55 px-2 py-1 rounded-full capitalize">
          <Icon size={12} className="text-[#2f8bff]" /> {TYPE_LABEL[place.type]}
        </span>
        <span className="absolute top-3 right-3 flex items-center gap-1 text-[11px] font-semibold text-white bg-black/55 px-2 py-1 rounded-full">
          <Star size={11} className="fill-[#f5a524] text-[#f5a524]" /> {place.rating}
        </span>
        {place.deliveryTime && (
          <span className="absolute bottom-3 left-3 flex items-center gap-1 text-[11px] font-bold text-[#05070c] bg-[#22c55e] px-2 py-1 rounded-full">
            <Zap size={11} className="fill-[#05070c]" /> {place.deliveryTime}
          </span>
        )}
      </div>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-white truncate">{place.name}</h3>
            <p className="text-[12px] text-[#8b95a5] flex items-center gap-1"><MapPin size={11} /> {place.area} · {place.distance}</p>
            {place.beds && <p className="text-[12px] text-[#c4ccd8] mt-0.5">{place.beds}</p>}
          </div>
          <PriceLabel place={place} />
        </div>

        {place.peopleHere && place.peopleHere.length > 0 && (
          <div className="flex items-center gap-2 mt-2.5">
            <div className="flex -space-x-2">
              {place.peopleHere.slice(0, 4).map((a, i) => (
                <img key={`${place.id}-p${i}`} src={a} alt="" className="w-6 h-6 rounded-full object-cover ring-2 ring-[#0c1420]" />
              ))}
            </div>
            <span className="text-[12px] text-[#22c55e] font-medium">{place.friends} friends here now</span>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {place.tags.map((t) => <span key={t} className="text-[11px] text-[#c4ccd8] bg-[#141c28] px-2 py-0.5 rounded-full">{t}</span>)}
        </div>

        <div className="flex gap-2 mt-3">
          <button onClick={doAction} className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${done ? 'bg-[#12322a] text-[#22c55e] ring-1 ring-[#22c55e]/30' : 'bg-[#2f8bff] text-white active:scale-[0.98]'}`}>
            {done ? <><CheckCircle2 size={15} /> Done</> : <><act.Icon size={15} /> {act.label}</>}
          </button>
          <button onClick={() => notify(`Tagged ${place.name}`)} className="px-3.5 py-2.5 rounded-lg ring-1 ring-[#2a3547] text-white active:scale-[0.98] transition-transform">
            <Tag size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Places() {
  const [filter, setFilter] = useState('All');
  const [booking, setBooking] = useState(null);
  const [cabOpen, setCabOpen] = useState(false);
  const [cabDest, setCabDest] = useState(null);
  const map = { Famous: 'famous', Hotels: 'hotel', Restaurants: 'restaurant', Food: 'food', 'B2B': 'b2b', 'Real Estate': 'realestate', Help: 'help', Beaches: 'beach' };
  const list = filter === 'All' ? places : places.filter((p) => p.type === map[filter]);

  return (
    <div className="pb-6">
      <div className="relative mx-4 mt-4 rounded-2xl overflow-hidden">
        <img src={currentPlace.image} alt="" className="w-full h-24 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/30 flex items-center px-4">
          <div className="flex-1">
            <p className="text-[11px] text-[#c4ccd8] flex items-center gap-1"><MapPin size={12} className="text-[#2f8bff]" /> You're at</p>
            <h2 className="text-[17px] font-bold text-white">{currentPlace.name}</h2>
            <p className="text-[12px] text-[#22c55e]">{nearbyPeople.length} people here now</p>
          </div>
          <button onClick={() => setCabOpen(true)} className="flex items-center gap-1.5 bg-[#2f8bff] text-white text-[12px] font-semibold px-3 py-2 rounded-full active:scale-95 transition-transform">
            <Car size={15} /> Book Cab
          </button>        </div>
      </div>

      <div className="px-4 mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-[#2f8bff]" />
          <h3 className="text-[14px] font-semibold text-white">Momos users nearby</h3>
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar">
          {nearbyPeople.map((p) => (
            <div key={p.name} className="flex flex-col items-center gap-1 shrink-0 w-16">
              <div className="relative">
                <Avatar src={p.avatar} size={56} online />
                <button onClick={() => notify(`Waved at ${p.name}`)} className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#2f8bff] flex items-center justify-center ring-2 ring-[#05070c]">
                  <Hand size={12} className="text-white" />
                </button>
              </div>
              <span className="text-[11px] text-white truncate w-full text-center">{p.name}</span>
              <span className="text-[9px] text-[#5c6675] truncate w-full text-center">{p.distance}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-4">
        {placeTypes.map((t) => (
          <button key={t} onClick={() => setFilter(t)} className={`shrink-0 px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${filter === t ? 'bg-white text-[#05070c]' : 'bg-[#141c28] text-[#c4ccd8]'}`}>{t}</button>
        ))}
      </div>

      <div className="px-4 space-y-4">
        {list.map((p) => (p.type === 'help' ? <HelpCard key={p.id} place={p} /> : <PlaceCard key={p.id} place={p} onBook={setBooking} onCab={(pl) => { setCabDest(pl); setCabOpen(true); }} />))}
      </div>

      {booking && <BookingSheet place={booking} onClose={() => setBooking(null)} />}
      {cabOpen && <CabSheet destination={cabDest} onClose={() => { setCabOpen(false); setCabDest(null); }} />}
    </div>
  );
}
